/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';

import { getInvoiceDetail, getPaymentAuthToken } from 'api/fullsteam';
import APFormGroup from 'components/FormFields/APFormGroup';
import InputField from 'components/FormFields/InputField';
import { styledTheme } from 'globalStyles/theme';
import PaymentModal from './PaymentModal';
import { roundToNearestCent, convertCentsToDollar } from './utils/currency';
import './styles.scss';

const dayjs = require('dayjs');

const INVOICE_DETAIL_DEFAULT = {
  id: 0,
  name: '',
  total: 0,
  paid: 0,
  balance: 0,
  due_date: '',
  companyName: '',
  companyAddress: '',
  ach_fee: 0,
  credit_fee: 0,
};

const parseAddress = (address) => {
  const { street, city, postcode, region, country } = address;
  return `${street} ${city} ${postcode} ${region} ${country}`;
};

const PaymentDetail = () => {
  const history = useHistory();
  const pathname = history.location?.pathname;
  const invoiceId = pathname.replace('/app/v2/invoice/pay/', '');
  const [invoiceDetail, setInvoiceDetail] = useState(INVOICE_DETAIL_DEFAULT);
  const [amount, setAmount] = useState(0);
  const [error, setError] = useState('');
  const [isProcessing, setProcessing] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const {
    ach_fee: achFee,
    credit_fee: creditFee,
    accepts_credit_platform_fee: acceptsCreditPlatform,
    accepts_ach_platform_fee: acceptsAchPlatformFee,
    global_ach_fee: achPercentage,
    global_credit_fee: creditPercentage,
    accepts_credit: acceptsCredit,
    accepts_ach: acceptsAch,
  } = invoiceDetail;
  const hasCreditPlatformFee = acceptsCredit && acceptsCreditPlatform && creditPercentage;
  const hasAchPlatformFee = acceptsAch && acceptsAchPlatformFee && achPercentage;
  const hasPlatformFee = (acceptsCredit && hasCreditPlatformFee) || (acceptsAch && hasAchPlatformFee);
  const creditFeeAmount = (hasCreditPlatformFee && creditPercentage && amount) ?
    roundToNearestCent(amount * (parseFloat(creditFee) * 0.01)) : 0;
  const achFeeAmount = (hasAchPlatformFee && achPercentage && amount) ?
    roundToNearestCent(amount * (parseFloat(achFee) * 0.01)) : 0;
  const creditAmount = roundToNearestCent(parseFloat(amount) + creditFeeAmount);
  const achAmount = roundToNearestCent(parseFloat(amount) + achFeeAmount);

  useEffect(() => {
    const handleInvoiceDetail = async () => {
      const detailResponse = await getInvoiceDetail({ invoiceId });
      if (detailResponse) {
        const {
          totals,
          invoice,
          next_payment_info,
          organization_contact,
          logo,
          payment_account_country_code,
        } = detailResponse.data;
        const { address, organization_name } = organization_contact || {};
        const {
          invoice_id: id,
          identifier = '',
          name,
          accepts_ach: isACH,
          accepts_credit: isCredit,
          accepts_offline: isCheck,
          global_ach_fee: ach_fee,
          global_credit_fee: credit_fee,
        } = invoice || {};
        const [nextPaymentAmount, nextPaymentDate] = next_payment_info || [];

        const {
          grand_total = 0,
          paid = 0,
          balance = 0,
        } = totals || {};

        setInvoiceDetail({
          ...invoice,
          currency: payment_account_country_code === 'CA' ? 'CAD' : 'USD',
          id,
          identifier,
          name,
          total: convertCentsToDollar(grand_total),
          paid: convertCentsToDollar(paid),
          balance: convertCentsToDollar(balance),
          due_amount: convertCentsToDollar(nextPaymentAmount),
          due_date: nextPaymentDate ? dayjs(nextPaymentDate).format('MM/DD/YYYY') : '',
          companyName: organization_name,
          companyAddress: parseAddress(address),
          isACH,
          isCredit,
          isCheck,
          ach_fee,
          credit_fee,
        });
        setAmount(convertCentsToDollar(nextPaymentAmount));
        setLogoUrl(logo?.full?.url || null);
      }
    };
    if (invoiceId) {
      handleInvoiceDetail();
    }
  }, [invoiceId]);

  // Default amounts has a lot of logic depending on if merchant accepts credit, ach or both.
  // and also whether credit and/or ach has platform fees.
  const getDefaultAmounts = () => {
    // If merchant accepts credit card, that is the default.
    if (acceptsCredit) {
      const defaultAmount = hasCreditPlatformFee ? creditAmount : amount;
      const defaultFeeAmount = hasCreditPlatformFee ? creditFeeAmount : 0;
      const defaultFee = hasCreditPlatformFee ? creditFee : 0;
      return { defaultAmount, defaultFeeAmount, defaultFee };
    }
    if (acceptsAch) {
      const defaultAmount = hasAchPlatformFee ? achAmount : amount;
      const defaultFeeAmount = hasAchPlatformFee ? achFeeAmount : 0;
      const defaultFee = hasAchPlatformFee ? achFee : 0;
      return { defaultAmount, defaultFeeAmount, defaultFee };
    }
    // Shouldn't occur, but handle weird exceptions.
    const defaultAmount = amount;
    const defaultFeeAmount =  0;
    const defaultFee = 0;
    return { defaultAmount, defaultFeeAmount, defaultFee };
  };

  const validateForm = () => {
    if (invoiceDetail.balance) {
      const amountValue = parseFloat(amount);
      if ((amountValue <= 0) || (amountValue > invoiceDetail.balance)) {
        setError(`Amount value must be between 0.01 and ${invoiceDetail.balance}`);
        return false;
      }
      setError('');
      return true;
    }
    return true;
  };

  const onContinueClick = async () => {
    if (validateForm()) {
      setProcessing(true);
      const { defaultAmount, defaultFeeAmount, defaultFee } = getDefaultAmounts();
      const payload = {
        amount: defaultAmount.toFixed(2),
        originalAmount: amount.toFixed(2),
        platformFee: defaultFeeAmount.toFixed(2),
        platformRate: defaultFee.toString(),
      };
      const authResponse = await getPaymentAuthToken(invoiceId, payload);
      setProcessing(false);
      if (authResponse?.data?.fs_response) {
        const { authenticationKey, expirationDateUtc, disableDupDetection = false } = authResponse.data.fs_response;
        const method = invoiceDetail.isCredit ? 'credit' : invoiceDetail.isACH ? 'ach' : 'cash';
        if (authenticationKey) {
          const invoiceData = {
            authenticationKey,
            expirationDateUtc,
            invoiceDetail,
            amount,
            disableDupDetection,
            method,
            creditAmount,
            creditFeeAmount,
            achAmount,
            achFeeAmount,
          };
          localStorage.setItem('invoice', JSON.stringify(invoiceData));
          history.push(`/app/v2/invoice/pay/${invoiceId}/process`);
        }
      }
    }
  };

  return (
    <PaymentModal
      invoiceId={invoiceId}
      opacity={isProcessing ? 0.8 : 1}
      onClose={() => {
        window.location.href = `/invoice/view/${invoiceId}`;
      }}
    >
      {isProcessing && (
        <section className="app_loader">
          <FadeLoader
            color={styledTheme.lighterGray}
            loading
            height={15}
            width={5}
            radius={5}
            margin={2}
          />
        </section>
      )}
      <div className="body-content container--small">
        {logoUrl && (
          <h3 className="invoice-modal__title">
            <img
              src={logoUrl}
              alt="Aisle Planner"
              className="ap-logo"
            />
          </h3>
        )}
        <div className="form-group invoice-detail mb-3">
          <div className="invoice-detail__header">
            <span>Invoice #{invoiceDetail.identifier}</span>
            <span>Invoice Name: {invoiceDetail.name}</span>
          </div>
          <div className="invoice-detail__content">
            <div className="invoice-detail__item">
              <span className="dashline" />
              <span className="info pr-2">Invoice Total</span>
              <span className="info pl-2">${invoiceDetail.total}</span>
            </div>
            <div className="invoice-detail__item">
              <span className="dashline" />
              <span className="info pr-2">Payment Due</span>
              <span className="info pl-2">${invoiceDetail.due_amount}</span>
            </div>
            <div className="invoice-detail__item">
              <span className="dashline" />
              <span className="info pr-2">Paid To Date</span>
              <span className="info pl-2">${invoiceDetail.paid}</span>
            </div>
            <div className="invoice-detail__item">
              <span className="dashline" />
              <span className="info pr-2">Due Date</span>
              <span className="info pl-2">{invoiceDetail.due_date}</span>
            </div>
            <div className="invoice-detail__item">
              <span className="dashline" />
              <span className="info pr-2">Remaining Balance</span>
              <span className="info pl-2">${invoiceDetail.balance}</span>
            </div>
          </div>
        </div>
        <APFormGroup
          className="form-group--payment-amount"
          grid={false}
        >
          <InputField
            name="amount"
            label={
              `Payment Amount (${!invoiceDetail.currency ? '' : invoiceDetail.currency === 'CAD' ? 'CAD' : 'USD'})`
            }
            value={amount}
            type="number"
            min="0.01"
            data-val-min="The payment amount must be larger than 0"
            data-val-max="The payment amount must be equal or lower than the invoice balance."
            max={invoiceDetail.balance}
            onChange={(e) => {
              setError('');
              const amountValue = e.target.value !== '' ? parseFloat(e.target.value) : 0;
              setAmount(roundUp(amountValue * 100, 10)/100);
            }}
            wrapperProps={{
              className: 'form-item--payment-amount',
            }}
            error={error}
          />
          <div className="text-danger field-validation-valid">
            {error}
          </div>
        </APFormGroup>
        {hasPlatformFee && (
          <div className="form-group invoice-detail mb-3">
            <div className="invoice-detail__header">
              <span>Payment options</span>
            </div>
            <div className="invoice-detail__content">

              {acceptsCredit && (
                <div className="invoice-detail__item">
                  Payments by Credit Card
                </div>
              )}
              {acceptsAch && (
                <div className="invoice-detail__item">
                  Payments by ACH/EFT
                </div>
              )}

              {(!acceptsCredit || !acceptsAch) && (
                <div className="invoice-detail__item empty" />
              )}

              {acceptsCredit && (
                <>
                  {hasCreditPlatformFee ? (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform fee</span>
                      <span className="info pl-2">{invoiceDetail.credit_fee}%</span>
                    </div>
                  ) : (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform fee</span>
                      <span className="info pl-2">0%</span>
                    </div>
                  )}
                </>
              )}

              {acceptsAch && (
                <>
                  {hasAchPlatformFee ? (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform fee</span>
                      <span className="info pl-2">{invoiceDetail.ach_fee}%</span>
                    </div>
                  ) : (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform fee</span>
                      <span className="info pl-2">0%</span>
                    </div>
                  )}
                </>
              )}


              {(!acceptsCredit || !acceptsAch) && (
                <div className="invoice-detail__item empty" />
              )}

              {acceptsCredit && (
                <>
                  {hasCreditPlatformFee ? (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform Fee Amount</span>
                      <span className="info pl-2">${creditFeeAmount.toFixed(2) ?? '0.00'}</span>
                    </div>
                  ) : (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform Fee Amount</span>
                      <span className="info pl-2">$0.00</span>
                    </div>
                  )}
                </>
              )}

              {acceptsAch && (
                <>
                  {hasAchPlatformFee ? (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform Fee Amount</span>
                      <span className="info pl-2">${achFeeAmount.toFixed(2) ?? '0.00'}</span>
                    </div>
                  ) : (
                    <div className="invoice-detail__item">
                      <span className="dashline" />
                      <span className="info pr-2">Platform Fee Amount</span>
                      <span className="info pl-2">$0.00</span>
                    </div>
                  )}
                </>
              )}

              {(!acceptsCredit || !acceptsAch) && (
                <div className="invoice-detail__item empty" />
              )}

              {acceptsCredit && (
                <div className="invoice-detail__item highlighted">
                  <span className="dashline" />
                  <span className="info pr-2">Total due (Credit card)</span>
                  <span className="info pl-2">${creditAmount.toFixed(2) ?? '0.00'}</span>
                </div>
              )}

              {acceptsAch && (
                <div className="invoice-detail__item highlighted">
                  <span className="dashline" />
                  <span className="info pr-2">Total due (ACH/EFT)</span>
                  <span className="info pl-2">${achAmount.toFixed(2) ?? '0.00'}</span>
                </div>
              )}

            </div>
          </div>
        )}
        <div className="form-group text-center">
          <button
            className="submit_button button button--lg"
            onClick={onContinueClick}
            disabled={!!error || isProcessing}
          >
            Continue
          </button>
        </div>
      </div>
    </PaymentModal>
  );
};

PaymentDetail.propTypes = {
};

PaymentDetail.defaultProps = {
};

export default PaymentDetail;

export const roundUp = (number) => {
  return Math.sign(number) * Math.round(Math.abs(number));
};
